import type { AppError, Result } from '@aibi/shared';
import { z } from 'zod';
import { sendJson, type FetchFn } from '../../api/http-client';

export const AUTH_API_ROUTES = {
  state: '/api/auth/state',
  setup: '/api/auth/setup',
  login: '/api/auth/login',
  logout: '/api/auth/logout',
} as const;

const AuthStateSchema = z.strictObject({
  setupRequired: z.boolean(),
  authenticated: z.boolean(),
});
export type AuthState = z.infer<typeof AuthStateSchema>;

/**
 * Sesion del administrador. La contrasena solo viaja en el cuerpo de la peticion: no se
 * guarda en estado persistente ni en `localStorage`. El token de sesion vive en una cookie
 * `HttpOnly` que el navegador gestiona y JavaScript no puede leer.
 */
export interface AuthApi {
  state(): Promise<Result<AuthState, AppError>>;
  setup(setupCode: string, password: string): Promise<Result<AuthState, AppError>>;
  login(password: string): Promise<Result<AuthState, AppError>>;
  logout(): Promise<Result<AuthState, AppError>>;
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetch: FetchFn;
}

/**
 * @example
 * const auth = createAuthApi({ baseUrl: '', fetch: window.fetch.bind(window) });
 * const state = await auth.state();
 */
export function createAuthApi({ baseUrl, fetch }: ApiClientOptions): AuthApi {
  const url = (path: string) => `${baseUrl}${path}`;
  return {
    state: () => sendJson(fetch, 'GET', url(AUTH_API_ROUTES.state), AuthStateSchema),
    setup: (setupCode, password) =>
      sendJson(fetch, 'POST', url(AUTH_API_ROUTES.setup), AuthStateSchema, {
        body: { setupCode: setupCode.trim().toUpperCase(), password },
      }),
    login: (password) =>
      sendJson(fetch, 'POST', url(AUTH_API_ROUTES.login), AuthStateSchema, {
        body: { password },
      }),
    logout: () => sendJson(fetch, 'POST', url(AUTH_API_ROUTES.logout), AuthStateSchema),
  };
}
